/*
{% comment %} SECTIONS: ywm-chat.js {% endcomment %}
{% comment %} https://github.com/ {% endcomment %}
{% comment %} LAST UPDATED: 20260811 {% endcomment %}

{%  comment %}
  USAGE: {%- render 'ywm-whatever', section:section, block:block -%} 
{% endcomment %}  
/*

/* ============================================================================
   ywm-chat.js — AirSpade
   Opens the Shopify Inbox chat widget from text links.

   ---------------------------------------------------------------------------
   BACKGROUND — why the previous openLiveChat() stopped working
   ---------------------------------------------------------------------------
   Shopify renamed the chat host element when they shipped the redesigned
   widget. It used to be <inbox-online-store-chat>; it is now <shopify-chat>.
   The bubble also lives inside an OPEN shadow root, which plain
   document.querySelector() cannot see through — you have to go via
   host.shadowRoot. Old forum snippets targeting #ShopifyChat or
   .chat-toggle are all stale for this reason.

   ---------------------------------------------------------------------------
   CONFIRMED LIVE MARKUP (verified on airspade.com, Aug 2026)
   ---------------------------------------------------------------------------
     <shopify-chat>
       #shadow-root (open)
         <div id="shopify-element-wrapper">
           <div class="activator-nudge activator-nudge--right activator-nudge--active">
             <button type="button" part="activator" class="activator"
                     aria-label="Chat" aria-expanded="false">
               <span class="activator__icon" data-icon-name="chat-bubble">…</span>
               <span class="activator__label">Chat</span>
               <span class="activator__indicator" aria-label="Unread replies"></span>
             </button>

   Notes from testing:
     - No <iframe> in the shadow root at rest; the bubble is a real button.
     - aria-expanded on the activator is the reliable open/closed signal.
     - Clicking the activator a second time does NOT close the panel, so a
       redundant click is harmless.

   ---------------------------------------------------------------------------
   USAGE
   ---------------------------------------------------------------------------
   Preferred:  <a href="/pages/contact-us" data-live-chat>Live Chat</a>
   Legacy:     <a href="javascript:void(0);" onclick="openLiveChat()">Live Chat</a>

   Both work. The preferred form degrades gracefully — if the widget is
   blocked (ad blockers hit Shopify's chat script fairly often) or JS fails,
   the link still lands somewhere useful instead of doing nothing.

   FALLBACK BEHAVIOUR
   If the widget never appears, the script sends the visitor to the link's
   OWN href. So each link declares its own graceful-degradation target in
   markup, rather than everything funnelling to one hardcoded page.

   It will never navigate to the page you are already on — that would look
   like a broken link at the exact moment the widget failed. On
   /pages/contact-us the same-page href is detected and suppressed, so give
   that link something that still helps, e.g.:

     <a href="#contact-form" data-live-chat>Live Chat</a>
     <a href="tel:8004827324" data-live-chat>Live Chat</a>

   A same-page href WITH a hash is allowed through (it scrolls, it doesn't
   reload). Links calling the legacy openLiveChat() with no href fall back
   to FALLBACK_URL below.

   ---------------------------------------------------------------------------
   INSTALL
   ---------------------------------------------------------------------------
   1. Save as assets/ywm-chat.js
   2. In layout/theme.liquid, before </body>:
        <script src="{{ 'ywm-chat.js' | asset_url }}" defer></script>

   DEBUG: run openLiveChat.debug() in the browser console.

   ---------------------------------------------------------------------------
   FUTURE REFERENCE — branding the chat bubble
   ---------------------------------------------------------------------------
   NOT IN USE. Client decision (Aug 2026): leave the bubble as Shopify's
   default. Documented here because it is newly possible and most search
   results still say it isn't.

   The activator exposes part="activator", so it CAN now be styled from the
   theme stylesheet — no JS, no !important wars with the widget's own CSS.
   Older forum threads saying the Inbox button cannot be styled predate this.

   Put any of the following in base.css or Theme Settings > Custom CSS
   (Custom CSS is preferable — theme settings survive a theme update, code
   edits do not):

     AirSpade yellow bubble:
     shopify-chat::part(activator){background:#FECB00;color:#121212}

     Square it off to match the site's button style:
     shopify-chat::part(activator){border-radius:0}

     Hide the bubble entirely and rely only on the text links:
     shopify-chat::part(activator){display:none}

   CAUTION on that last one: hide the ACTIVATOR PART, never the host.
   shopify-chat{display:none} would also hide the panel that this script
   opens, which would silently break every "Live Chat" link on the site.
   The ::part() approach targets only the bubble. Test before shipping —
   Shopify may render the panel inside the same wrapper.

   Nudge bubble ("activator-nudge" in the markup above) is not exposed as a
   part, so it is not styleable from outside. It can only be turned off in
   Shopify admin under Inbox > chat settings.
   ============================================================================ */

(function () {
  'use strict';

  var TAG = '[ywm-chat]';

  /* --- logging -------------------------------------------------------------
     Set to true to turn console output back on while troubleshooting.
     Off by default so the script stays silent in production.

     Using a flag rather than commenting each line out: one edit restores
     every message at once, and there is no risk of a half-uncommented
     line breaking the file later.                                        */
  var DEBUG = false;

  function log(msg, extra) {
    if (!DEBUG) return;
    if (extra !== undefined) console.log(TAG + ' ' + msg, extra);
    else console.log(TAG + ' ' + msg);
  }

  function warn(msg, extra) {
    if (!DEBUG) return;
    if (extra !== undefined) console.warn(TAG + ' ' + msg, extra);
    else console.warn(TAG + ' ' + msg);
  }

  // Current element first, legacy names kept so this survives a rollback.
  var HOST_SELECTORS = ['shopify-chat', 'inbox-online-store-chat', '#ShopifyChat', '#shopify-chat'];

  // Current selectors first, legacy after. Falls back to any button in the host.
  var TOGGLE_SELECTORS = [
    'button[part="activator"]',   // current
    'button.activator',           // current
    'button.chat-toggle',         // legacy
    '[class*="chat-toggle"]',     // legacy
    'button[aria-label*="chat" i]',
    'button[aria-label*="message" i]'
  ];

  // Backstop only — used when the trigger has no usable href of its own
  // (e.g. legacy onclick="openLiveChat()" links, or a programmatic call).
  // A link's own href always wins. Set to null to never navigate.
  var FALLBACK_URL = '/pages/contact-us';

  var MAX_WAIT = 8000;   // ms to keep retrying while the widget loads
  var INTERVAL = 250;    // ms between retries

  /* --- find the chat host element ----------------------------------------- */
  function getHost() {
    for (var i = 0; i < HOST_SELECTORS.length; i++) {
      var el = document.querySelector(HOST_SELECTORS[i]);
      if (el) return el;
    }
    return null;
  }

  /* --- search a root for the activator button ------------------------------
     trusted = this root belongs to the chat widget, so a bare <button> is
     safe to click. Never pass trusted:true for the document.               */
  function findToggle(root, depth, trusted) {
    if (!root || depth > 5) return null;

    for (var i = 0; i < TOGGLE_SELECTORS.length; i++) {
      var btn = root.querySelector(TOGGLE_SELECTORS[i]);
      if (btn) return btn;
    }

    // Class-name-agnostic fallback, only inside a known chat host. This is
    // what makes the script survive Shopify's next rename.
    if (trusted) {
      var generic = root.querySelector('button, [role="button"]');
      if (generic) return generic;
    }

    // Recurse into nested shadow roots.
    var all = root.querySelectorAll('*');
    for (var j = 0; j < all.length; j++) {
      if (all[j].shadowRoot) {
        var found = findToggle(all[j].shadowRoot, depth + 1, trusted);
        if (found) return found;
      }
    }
    return null;
  }

  /* --- is the panel already open? ------------------------------------------
     Only a literal "true" counts. If Shopify ever stops maintaining the
     attribute we fail toward clicking, which testing showed is a no-op.   */
  function isOpen(toggle) {
    return !!toggle && toggle.getAttribute('aria-expanded') === 'true';
  }

  /* --- work out where to send someone if the widget never loads ------------
     Prefers the triggering link's own href, falls back to FALLBACK_URL, and
     returns null when there is nowhere sensible to go — including when the
     target resolves to the page we are already on.                        */
  function resolveFallback(link) {
    var url = link ? link.getAttribute('href') : null;

    // javascript: and bare # are not destinations.
    if (!url || url === '#' || /^javascript:/i.test(url)) url = FALLBACK_URL;
    if (!url) return null;

    // Let the browser normalise it so we can compare against the current URL.
    var probe = document.createElement('a');
    probe.href = url;

    var samePage = probe.protocol === window.location.protocol &&
                   probe.host === window.location.host &&
                   probe.pathname === window.location.pathname;

    // Same page with no hash = a pointless reload. A hash is fine, it scrolls.
    if (samePage && !probe.hash) return null;

    return url;
  }

  /* --- public function ---------------------------------------------------- */
  window.openLiveChat = function (event) {
    var link = null;

    if (event) {
      if (typeof event.preventDefault === 'function') event.preventDefault();
      var target = event.target || event.currentTarget;
      if (target && target.closest) link = target.closest('a[href]');
    }

    var fallback = resolveFallback(link);
    var waited = 0;

    (function attempt() {
      try {
        var host = getHost();
        var toggle = null;

        if (host && host.shadowRoot) {
          toggle = findToggle(host.shadowRoot, 0, true);
        } else if (host) {
          warn('host found but its shadowRoot is not accessible (closed).');
        }

        // Last-ditch: crawl the document for any shadow-rooted toggle.
        // trusted:false so we never click an unrelated theme button.
        if (!toggle && waited >= MAX_WAIT - INTERVAL) toggle = findToggle(document, 0, false);

        if (toggle) {
          if (isOpen(toggle)) { log('chat is already open'); return; }
          toggle.click();
          return;
        }
      } catch (err) {
        warn('error while opening chat:', err);
      }

      waited += INTERVAL;

      if (waited >= MAX_WAIT) {
        warn('chat widget not found after ' + MAX_WAIT + 'ms. Run openLiveChat.debug().');
        if (fallback) window.location.href = fallback;
        else warn('no usable fallback destination — staying put.');
        return;
      }

      setTimeout(attempt, INTERVAL);
    })();
  };

  /* --- diagnostic helper: openLiveChat.debug() -----------------------------
     Logs unconditionally, ignoring the DEBUG flag. It only ever runs when
     you type it into the console, so it never adds noise on its own — and
     having to flip a flag before you can diagnose anything defeats the
     point of the helper.                                                  */
  window.openLiveChat.debug = function () {
    var host = getHost();
    var root = host ? host.shadowRoot : null;
    var out = {
      host: host,
      hostTag: host ? host.tagName.toLowerCase() : null,
      shadowRoot: root,
      toggle: root ? findToggle(root, 0, true) : null,
      ariaExpanded: null,
      markup: root ? root.innerHTML : null
    };
    if (out.toggle) out.ariaExpanded = out.toggle.getAttribute('aria-expanded');
    console.log(TAG + ' debug', out);
    return out;
  };

  /* --- auto-bind links ----------------------------------------------------
     Delegated listener on document, so it also covers links that appear
     later: the mobile drawer, the duplicated footer, and section re-renders
     in the theme editor. A per-link forEach at DOMContentLoaded would miss
     all three.                                                            */
  function bindLinks() {
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      var link = e.target.closest('[data-live-chat], a[href*="?chat"], a[href="#live-chat"]');
      if (!link) return;
      window.openLiveChat(e);
    }, false);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindLinks);
  } else {
    bindLinks();
  }
})();